<template>
  <li v-if="isCreated" class="l-side-rooms__item">
    <router-link
      :to="{
        name: 'room',
        query: {
          partner: partner.id,
          project: project.id,
          r: role.query,
          proxyUserId: $route.query.proxyUserId
        }
      }"
      class="l-side-rooms__link"
      exact
    >
      <div v-if="isCompany" class="l-side-rooms__avatar">
        <Avatar :src="partner.avatar" />
      </div>
      <div class="l-side-rooms__name">
        {{ roomName }}
      </div>
      <transition name="v-fade--delay">
        <Badge v-if="!isCurrent && roomCountUnread" variant="count">
          {{ roomCountUnread | roundNumberText(100) }}
        </Badge>
      </transition>
    </router-link>
  </li>
</template>

<script>
import Avatar from '@/components/Avatar';
import Badge from '@/components/Badge';
import utils from '@/utils';
import {
  EXTERNAL_SERVICE,
  EXTERNAL_SERVICE_ACTION
} from '@/analytics/constants';
import { reportExternalServiceError } from '@/analytics/reportExternalServiceError';

export default {
  props: {
    partner: {
      type: Object,
      required: true
    },
    project: {
      type: Object,
      required: true
    }
  },
  components: {
    Avatar,
    Badge
  },
  data() {
    return {
      listener: null,
      defaultUnreadCount: 0
    };
  },
  computed: {
    role() {
      return this.$store.state.role;
    },
    room() {
      let room = null;
      if (this.roomId) {
        for (let i = 0; i < this.rooms.length; i++) {
          if (this.rooms[i].id === this.roomId) {
            room = this.rooms[i];
            break;
          }
        }
      }
      return room;
    },
    roomCountUnread() {
      return this.room ? this.room.countUnread : 0;
    },
    roomId() {
      return utils.getRoomId({
        partnerId: this.partner.id,
        projectId: this.project.id,
        roleName: this.role.name,
        userId: this.$store.getters.effectiveSub
      });
    },
    roomLastDatetime() {
      return this.room ? this.room.lastDatetime : null;
    },
    roomManualUnreadDatetime() {
      return this.room ? this.room.companyManualUnreadDatetime : null;
    },
    roomManualUnreadMessageId() {
      return this.room ? this.room.companyManualUnreadMessageId : null;
    },
    roomName() {
      let name = '';
      if (this.isCompany) {
        name = this.partner.name;
      } else if (this.isInfluencer) {
        name = utils.getRoomName(this.room);
      }
      return name;
    },
    rooms() {
      return this.$store.state.rooms;
    },
    user() {
      return this.$store.state.user;
    },
    isCompany() {
      return utils.isCompany(this.role.name);
    },
    isCreated() {
      // インフルエンサーにはルームを作る権限がないので、代理店側がルームを作るまでは非表示にする必要がある
      // まだ作られていないルームにメッセージ投稿するとエラーが出るため
      return utils.isCreatedRoom(this.room);
    },
    isCurrent() {
      return this.roomId === this.$store.getters.currentRoomId;
    },
    isInfluencer() {
      return utils.isInfluencer(this.role.name);
    }
  },
  watch: {
    roomLastDatetime: {
      handler(value) {
        if (value) {
          this.setUnreadMessagesListener();
        }
      },
      immediate: true
    },
    roomManualUnreadDatetime() {
      this.updateRoomUnreadCount();
    },
    roomManualUnreadMessageId() {
      this.updateRoomUnreadCount();
    }
  },
  beforeDestroy() {
    if (this.listener) {
      this.listener();
    }
  },
  methods: {
    hasManualUnread() {
      if (
        !this.isCompany ||
        !this.roomManualUnreadMessageId ||
        !this.roomManualUnreadDatetime ||
        !this.roomLastDatetime
      ) {
        return false;
      }
      return (
        utils.compareTimestamp(
          this.roomManualUnreadDatetime,
          this.roomLastDatetime
        ) <= 0
      );
    },
    setUnreadMessagesListener() {
      if (this.listener) {
        // 前のlastDatetimeの時のリスナーを解除する
        this.listener();
      }
      const ref = this.$firebase.db.collection(
        `rooms/${this.room.id}/messages`
      );
      this.listener = ref
        .where('datetime', '>', this.roomLastDatetime)
        .onSnapshot(
          snapshot => {
            this.defaultUnreadCount = snapshot.size;
            this.updateRoomUnreadCount();
          },
          error =>
            reportExternalServiceError({
              service: EXTERNAL_SERVICE.FIRESTORE,
              action_type: EXTERNAL_SERVICE_ACTION.CHAT_UNREAD_COUNT_LISTEN,
              error
            })
        );
    },
    updateRoomUnreadCount() {
      const count =
        this.isCompany &&
        this.defaultUnreadCount === 0 &&
        this.hasManualUnread()
          ? 1
          : this.defaultUnreadCount;
      this.$store.dispatch('setRoom', {
        countUnread: count,
        id: this.room.id
      });
    }
  }
};
</script>
